import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { opTime } from 'utils/dates';
import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { OnSubmitArgs, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpFormDrawer } from 'new-components/DLS/OpFormDrawer/OpFormDrawer';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useEndpointPermissions } from 'utils/customHooks/useEndpointPermissions';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';

import { DATE_TIME_FORMAT } from '../../../../../constants/dates';
import STATUS from '../../../../../constants/status';
import { DRAWER_WIDTH } from '../../../../../constants/ui';
import { SIGNED_IN_VISITOR_ID } from '../../../../../constants/userActivities';
import { fetchCustomFields } from '../../../../../store/slices/customFieldsSlice';
import {
  fetchLocations,
  LocationsState,
} from '../../../../../store/slices/locationsSlice';
import { fetchRedFlags } from '../../../../../store/slices/redFlagSlice';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../../store/slices/visitorSearchSlice';
import { fetchVisits } from '../../../../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../../../../store/store';
import { ConfirmDeniedData } from '../../../../../types/denyTypes';
import { FormFieldWorkflow } from '../../../../../types/formFieldTypes';
import { Location, LocationWorkflow } from '../../../../../types/locationTypes';
import { VisitorInvite } from '../../../../../types/visitorInvitationTypes';
import { VisitorType } from '../../../../../types/VisitorTypeTypes';
import { VisitorWorkflowFields } from '../../../../../types/visitorWorkflowTypes';
import {
  ExcludeHostsResponse,
  HostUser,
  LocationWorkflowResponse,
  UsersResponse,
} from '../../../../../types/visitorsDrawerTypes';
import {
  formatFullName,
  transformNullToEmptyString,
} from '../../../../../utils/utils';
import VisitorList from '../formComponents/VisitorList';

import SignInManuallyAccessControlModal from './footer/SignInManuallyAccessControlModal';

interface EnterManuallyComponentProps {
  open: boolean;
  locationIdRef: number;
  onClose: () => void;
  newVisitorName?: {
    firstName: string;
    middleName: string;
    lastName: string;
    dob?: string;
  };
  setNewVisitorName: (
    filter:
      | {
          firstName: string;
          middleName: string;
          lastName: string;
          dob?: string;
        }
      | undefined,
  ) => void;
}

interface VisitorWorkflowFieldsResponse {
  data?: VisitorWorkflowFields[] | null;
}

interface FormFieldWorkflowResponse {
  data?: FormFieldWorkflow[] | null;
}

interface VisitorTypesResponse {
  data?: VisitorType[] | null;
}

interface InvitationResponse {
  data?: Array<{
    id: number;
    visitors?: Array<{ id: number | string; middleName?: string | null }>;
  }> | null;
}

interface FormVisitor {
  id: number | string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  email?: string | null;
  mobilePhone?: string | null;
  status?: number | null;
  reasonId?: number;
  details?: string;
  addToBlockList?: boolean;
  addToWatchList?: boolean;
  error?: string;
}

const SignInManuallyDrawer = ({
  open,
  locationIdRef,
  onClose,
  newVisitorName,
  setNewVisitorName,
}: EnterManuallyComponentProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId } = useIdentityAndUser();

  const { isValid: hasVisitorlogWrite } = useValidateScopes([
    'o{orgId}-visitorVisitorLog:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasAllVisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { hasReadPermission: hasRedFlagRead } =
    useEndpointPermissions('listAllRedFlag');
  const { isValid: hasDenyWrite } = useValidateScopes([
    'o{orgId}-visitorDeny:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];
  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as NonNullable<LocationsState['globalLocation']>['id'] | undefined;
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hosts, setHosts] = useState<HostUser[]>([]);
  const [excludedHostIds, setExcludedHostIds] = useState<number[]>([]);
  const [visitorTypes, setVisitorTypes] = useState<VisitorType[]>([]);
  const [locationVisitorTypes, setLocationVisitorTypes] = useState<
    VisitorType[]
  >([]);
  const [locationDefaultWorkflow, setLocationDefaultWorkflow] =
    useState<LocationWorkflow>();
  const [signInWorkflowFields, setSignInWorkflowFields] =
    useState<VisitorWorkflowFields>();
  const [signInWorkflowCustomFields, setSignInWorkflowCustomFields] = useState<
    FormFieldWorkflow[]
  >([]);
  const [isAccessControlModalOpen, setIsAccessControlModalOpen] =
    useState(false);
  const [newStatus, setNewStatus] = useState<number>(0);

  const isInitialLoad = useRef(true);

  const [form] = OpForm.useForm();
  const selectedVisitorTypeId = OpForm.useWatch('visitorTypeId', form);

  const initialValues = {
    locationId: locationIdRef,
    visitorTypeId: visitorTypes[0]?.id,
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const locationWorkflowResponse = (await heliumReceive(
          'locationConfig',
          [orgId, locationIdRef],
        )) as unknown as LocationWorkflowResponse<LocationWorkflow>;
        const locationVisitorTypesReponse = (await heliumReceive(
          'listAllVisitorTypeWorkflow',
          [orgId, locationIdRef],
        )) as unknown as VisitorTypesResponse;

        const locationWorkflow = locationWorkflowResponse.data?.[0];
        if (!locationWorkflow?.SignInWorkflowId) {
          return;
        }

        const signInWorkflowFieldsResponse = (await heliumReceive(
          'getVisitorWorkflowFields',
          [orgId, locationWorkflow.SignInWorkflowId],
        )) as unknown as VisitorWorkflowFieldsResponse;
        const signInWorkflowCustomFieldsResponse = (await heliumReceive(
          'getVisitorWorkflowFieldsCustom',
          [orgId, locationWorkflow.SignInWorkflowId],
        )) as unknown as FormFieldWorkflowResponse;

        setLocationDefaultWorkflow(locationWorkflow); // Default location workflow
        setLocationVisitorTypes(locationVisitorTypesReponse.data ?? []); // Visitor Types that contains workflows by visitor type
        setSignInWorkflowFields(signInWorkflowFieldsResponse.data?.[0]);
        setSignInWorkflowCustomFields(
          signInWorkflowCustomFieldsResponse.data ?? [],
        );

        dispatch(fetchCustomFields({ orgId }));

        if (orgId && locations.length === 0) {
          dispatch(fetchLocations({ orgId, status: 1 }));
        }

        const [users, excludedHosts] = (await Promise.all([
          heliumReceive('listUsers', [orgId]),
          heliumReceive('listAllExcludeHosts', [orgId]),
        ])) as [UsersResponse, ExcludeHostsResponse];

        const activeUsers = (users.data ?? []).filter((u) => u.status === 'A');
        const excludedIds = (excludedHosts.data ?? []).map((ex) => ex.userId);

        setHosts(activeUsers);
        setExcludedHostIds(excludedIds);

        const visitorTypesResponse = (await heliumReceive('getVisitorTypes', [
          orgId,
        ])) as unknown as VisitorTypesResponse;
        setVisitorTypes(visitorTypesResponse.data ?? []);
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, locationIdRef, dispatch, locations.length]);

  useEffect(() => {
    const handleVisitorTypeChange = async () => {
      // Return if datas are not ready
      if (!selectedVisitorTypeId || locationVisitorTypes.length === 0) return;

      // Return if it's initial load (fields are already set during initial data setting)
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        return;
      }

      const selectedVisitorType = locationVisitorTypes.find(
        (v) => v.id === selectedVisitorTypeId,
      );

      const hasVisitorTypeSignInWorkflow = (locationVisitorTypes ?? []).some(
        (type) =>
          type.workflow?.SignInWorkflowId &&
          type.workflow.SignInWorkflowId !== -1,
      );

      // Return if selected visitor type doesn't exist in location visitor types or any workflow didn't get allocated to any visitor type
      if (!selectedVisitorType || !hasVisitorTypeSignInWorkflow) return;

      const workflowId = selectedVisitorType.workflow?.SignInWorkflowId;
      const finalWorkflowId =
        workflowId !== -1
          ? workflowId
          : locationDefaultWorkflow?.SignInWorkflowId;

      if (!finalWorkflowId) return;

      try {
        const fieldConfig = (await heliumReceive('getVisitorWorkflowFields', [
          orgId,
          finalWorkflowId,
        ])) as unknown as VisitorWorkflowFieldsResponse;
        setSignInWorkflowFields(fieldConfig.data?.[0]);

        const fieldCustom = (await heliumReceive(
          'getVisitorWorkflowFieldsCustom',
          [orgId, finalWorkflowId],
        )) as unknown as FormFieldWorkflowResponse;
        setSignInWorkflowCustomFields(fieldCustom.data ?? []);

        if (hasVisitorTypeSignInWorkflow) {
          notificationService.show(
            'info',
            t('signInManuallyDrawer.notification.changeFieldsNotification'),
          );
        }
      } catch {
        notificationService.show(
          'error',
          t('signInManuallyDrawer.notification.changeFieldsError'),
        );
      }
    };
    handleVisitorTypeChange();
  }, [
    orgId,
    selectedVisitorTypeId,
    locationDefaultWorkflow,
    locationVisitorTypes,
    t,
    dispatch,
  ]);

  const renderFormFields = () => {
    return signInWorkflowCustomFields.map((formField, index) => {
      const capitalizedLabel = formField.name;
      if (formField.values) {
        const options = formField.values.map((option) => ({
          label: option.display || option.name,
          value: option.value,
        }));

        return (
          <OpForm.Select
            label={capitalizedLabel}
            name={formField.id}
            options={options}
            key={formField.id || index.toString()}
            rules={[{ required: formField.required === 1 }]}
            placeholder={
              formField.placeholder ? formField.placeholder : undefined
            }
            allowClear
          />
        );
      }
      return (
        <OpForm.TextAreaInput
          label={capitalizedLabel}
          name={formField.id}
          key={formField.id || index.toString()}
          rows={4}
          rules={[{ required: formField.required === 1 }]}
          placeholder={
            formField.placeholder ? formField.placeholder : undefined
          }
        />
      );
    });
  };

  const handleConfirmedDenied = ({
    selectedVisitors,
    reasonId: deniedReasonId,
    details: deniedDetails,
    addToBlockList: deniedBlockList,
    addToWatchList: deniedWatchList,
  }: ConfirmDeniedData) => {
    const visitors = (form.getFieldValue('visitors') || []) as FormVisitor[];
    const updatedVisitors = visitors.map((visitor) => {
      const isSelected = selectedVisitors.includes(String(visitor.id));

      if (isSelected) {
        return {
          ...visitor,
          status: STATUS.DENIED_ENTRY.id,
          reasonId: deniedReasonId,
          details: deniedDetails,
          addToBlockList: deniedBlockList,
          addToWatchList: deniedWatchList,
        };
      }
      if (visitor.status === STATUS.DENIED_ENTRY.id) {
        const {
          status,
          reasonId,
          details,
          addToBlockList,
          addToWatchList,
          ...rest
        } = visitor;
        return rest;
      }

      return visitor;
    });

    form.setFieldsValue({ visitors: updatedVisitors });
  };

  const validateVisitorModalFields = useCallback(
    (visitors: FormVisitor[], workflowFields?: VisitorWorkflowFields) => {
      if (!workflowFields) return visitors;

      const middleNameRequired =
        workflowFields.middleName?.included === 1 &&
        workflowFields.middleName?.required === 1;
      const emailRequired =
        workflowFields.email?.included === 1 &&
        workflowFields.email?.required === 1;
      const phoneNameRequired =
        workflowFields.visitPhone?.included === 1 &&
        workflowFields.visitPhone?.required === 1;

      return visitors.map((v) => {
        const missingFields =
          (middleNameRequired && !v.middleName?.trim()) ||
          (emailRequired && !v.email?.trim()) ||
          (phoneNameRequired && !v.mobilePhone?.trim());

        return {
          ...v,
          error: missingFields ? t('notification.validationError') : undefined,
        };
      });
    },
    [t],
  );

  // Validate visitor modal fields when visitor type changes
  useEffect(() => {
    const visitors = form.getFieldValue('visitors') || [];
    form.setFieldsValue({
      visitors: validateVisitorModalFields(visitors, signInWorkflowFields),
    });
  }, [
    form,
    selectedVisitorTypeId,
    signInWorkflowFields,
    validateVisitorModalFields,
  ]);

  const handleSubmit = useCallback(
    async ({ values }: OnSubmitArgs) => {
      setIsSubmitting(true);

      // Get timezone for sign in time
      const selectedLocation = locations.find(
        (loc) => loc.id === locationIdRef,
      );
      const locationTz =
        selectedLocation?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Current time in UTC
      const signInTimeUTC = opTime()
        .tz(locationTz)
        .utc()
        .format(DATE_TIME_FORMAT);

      try {
        if (values.visitors === undefined || values.visitors.length === 0) {
          notificationService.show(
            'error',
            t('signInManuallyDrawer.notification.signInError'),
          );
          setIsSubmitting(false);
          return;
        }

        const validated = validateVisitorModalFields(
          values.visitors,
          signInWorkflowFields,
        );
        if (validated.some((v) => v.error)) {
          form.setFieldsValue({ visitors: validated });
          notificationService.show('error', t('notification.validationError'));
          setIsSubmitting(false);
          return;
        }

        const transformedValues = transformNullToEmptyString(values);
        const normalizedValues = transformedValues as Record<
          string,
          unknown
        > & {
          visitors?: FormVisitor[];
        };

        // Filter out numeric keys directly
        const filteredValues = Object.keys(normalizedValues).reduce(
          (acc, key) => {
            const newAcc = { ...acc };
            // Exclude Custom Fields
            if (isNaN(Number(key))) {
              newAcc[key] = normalizedValues[key];
            }

            // Exclude middleName, email, or mobilePhone from payload if scope is turned off
            if (
              key === 'visitors' &&
              Array.isArray(normalizedValues.visitors)
            ) {
              newAcc.visitors = normalizedValues.visitors.map((visitor) => {
                const visitorPayload = { ...visitor };

                if (signInWorkflowFields?.middleName?.included === 0) {
                  delete visitorPayload.middleName;
                }
                if (signInWorkflowFields?.email?.included === 0) {
                  delete visitorPayload.email;
                }
                if (signInWorkflowFields?.visitPhone?.included === 0) {
                  delete visitorPayload.mobilePhone;
                }

                return visitorPayload;
              });
            }
            return newAcc;
          },
          {} as Record<string, unknown> & { visitors?: FormVisitor[] },
        );

        const filteredVisitors = filteredValues.visitors ?? [];

        // Immediately sign in visitors upon sending an invitation.
        const inviteResponse = (await heliumSend('newInvitation', [orgId], {
          ...filteredValues,
          sendHostEmail: 0,
          visitors: filteredVisitors.map((visitor) => ({
            firstName: visitor.firstName?.trim(),
            middleName: String(visitor.id), // Will be replaced with real middle name from filteredValues
            lastName: visitor.lastName?.trim(),
            email: visitor.email?.trim(),
            mobilePhone: visitor.mobilePhone?.trim(),
            status: STATUS.PENDING.id,
          })),
        })) as unknown as InvitationResponse;

        const visit = inviteResponse.data?.[0];
        if (!visit?.id) {
          throw new Error('Sign-in response is missing visit id');
        }
        let hasDeniedVisitor = false;

        await Promise.all(
          (visit.visitors ?? []).map(async (visitor) => {
            if (!visitor.id) return;
            const matched = filteredVisitors.find(
              (v) => String(v.id) === String(visitor.middleName),
            );
            if (!matched) return;

            if (matched.status === STATUS.DENIED_ENTRY.id) {
              hasDeniedVisitor = true;
              await heliumSend(
                'updateVisitors',
                [orgId, visit.id, visitor.id],
                {
                  signIn: signInTimeUTC,
                  middleName: matched.middleName ?? '',
                  status: STATUS.DENIED_ENTRY.id,
                },
              );
              await heliumSend('denyEntry', [orgId, visit.id], {
                visitorId: Number(visitor.id),
                deniedBy: Number(userId),
                reasonId: Number(matched.reasonId),
                details: matched.details,
              });
              if (matched.addToBlockList || matched.addToWatchList) {
                await heliumSend('newRedFlag', [orgId], {
                  level: matched.addToBlockList ? '1' : '2',
                  effectiveDate: signInTimeUTC,
                  visitors: [
                    {
                      firstName: matched.firstName,
                      lastName: matched.lastName,
                      middleName: matched.middleName,
                    },
                  ],
                });

                // Dispatch custom event to notify Red Flag Report page
                window.dispatchEvent(
                  new CustomEvent('visitorRedFlagAdded', {
                    detail: { orgId, level: matched.addToBlockList ? 1 : 2 },
                  }),
                );
              }
            } else {
              await heliumSend(
                'updateVisitors',
                [orgId, visit.id, visitor.id],
                {
                  signIn: signInTimeUTC,
                  middleName: matched.middleName ?? '',
                  status: STATUS.SIGNED_IN.id,
                },
              );
            }
          }),
        );

        await heliumSend('updateVisit', [orgId, visit.id], {
          signIn: signInTimeUTC,
          status: hasDeniedVisitor
            ? STATUS.DENIED_ENTRY.id
            : STATUS.SIGNED_IN.id,
        });

        // Fetch RedFlags so that the table can be updated. Guarded on
        // redFlagReadScopes because the helium GET /redflag route rejects roles
        // without red flag read (e.g. dashboard-only) with a 403.
        if (hasRedFlagRead) {
          dispatch(fetchRedFlags({ orgId }));
        }

        const customFieldPromises = [];
        for (const key in values) {
          if (Object.prototype.hasOwnProperty.call(values, key)) {
            const fieldVal = values[key];
            if (!isNaN(Number(key)) && fieldVal !== undefined) {
              customFieldPromises.push(
                heliumSend('createVisitorData', [orgId, visit.id], {
                  fieldId: key,
                  value: fieldVal,
                }).catch(() => {}),
              );
            }
          }
        }
        await Promise.all(customFieldPromises);

        // log activity
        const fullNameList = values.visitors
          .map((visitor: VisitorInvite) =>
            formatFullName(
              visitor.firstName,
              visitor.middleName,
              visitor.lastName,
            ),
          )
          .join(', ');
        if (!userId) {
          notificationService.show(
            'warning',
            t('notification.activityLogMasterModeWarning'),
          );
        } else {
          await heliumSend('newActivityLog', [orgId], {
            userId,
            activityId: SIGNED_IN_VISITOR_ID,
            details: fullNameList,
          });
        }
        notificationService.show(
          'success',
          t('signInManuallyDrawer.notification.signInSuccess'),
        );

        dispatch(fetchVisits({ orgId }));
        dispatch(searchVisitorsToday({ orgId }));
        if (Object.keys(lastSearchParams).length !== 0) {
          dispatch(
            searchVisitors({
              ...lastSearchParams,
              orgId,
              locationId: lastSearchParams.locationId || globalLocationId!,
            }),
          );
        }
        form.resetFields();
        onClose();
      } catch {
        notificationService.show(
          'error',
          t('signInManuallyDrawer.notification.submissionError'),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      orgId,
      form,
      userId,
      t,
      globalLocationId,
      lastSearchParams,
      signInWorkflowFields,
      dispatch,
      onClose,
      validateVisitorModalFields,
      locations,
      locationIdRef,
      hasRedFlagRead,
    ],
  );

  const filteredHosts = (
    hasAllVisitorsWrite ? hosts : hosts.filter((host) => host.id === userId)
  ).filter((host) => !excludedHostIds.includes(host.id));

  return (
    <>
      <OpFormDrawer
        form={form}
        title={t('signInManuallyDrawer.title')}
        size={DRAWER_WIDTH}
        open={open}
        onClose={() => {
          onClose();
          setNewVisitorName(undefined);
        }}
        formComponent={
          <OpForm
            form={form}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            hasError={false}
            defaultButtons={{
              leftContent: hasDenyWrite ? (
                <OpButton
                  type="primary"
                  danger
                  ghost
                  style={{ width: 'auto' }}
                  disabled={loading || isSubmitting}
                  onClick={() => {
                    if (
                      form.getFieldValue('visitors') === undefined ||
                      form.getFieldValue('visitors').length === 0
                    ) {
                      notificationService.show(
                        'error',
                        t('signInManuallyDrawer.notification.signInError'),
                      );
                      return;
                    }
                    setIsAccessControlModalOpen(true);
                    setNewStatus(Number(STATUS.DENIED_ENTRY.id));
                  }}
                >
                  {t('signInManuallyDrawer.footer.denyEntry')}
                </OpButton>
              ) : undefined,
            }}
            isLoading={loading || isSubmitting}
          >
            <VisitorList
              signInWorkflowFields={signInWorkflowFields}
              isInviting={false}
              isLoading={loading || isSubmitting}
              newVisitorName={newVisitorName}
              setNewVisitorName={setNewVisitorName}
              selectedVisitorTypeId={selectedVisitorTypeId}
              onVisitorTypeChange={(value) =>
                form.setFieldsValue({ visitorTypeId: value })
              }
              locationVisitorTypes={locationVisitorTypes}
            />

            <OpForm.Select
              label={t('signInManuallyDrawer.visitorType.label')}
              name="visitorTypeId"
              rules={[
                {
                  required: true,
                  message: t('signInManuallyDrawer.visitorType.required'),
                },
              ]}
              placeholder={t('signInManuallyDrawer.visitorType.placeholder')}
              options={visitorTypes.map((type) => ({
                label: t(type.languageCode ?? ''),
                value: type.id,
              }))}
            />

            <OpForm.Select
              label={t('signInManuallyDrawer.location.label')}
              name="locationId"
              rules={[
                {
                  required: true,
                  message: t('signInManuallyDrawer.location.required'),
                },
              ]}
              placeholder={t('signInManuallyDrawer.location.placeholder')}
              options={locations.map((location: Location) => ({
                label: location.name,
                value: location.id,
              }))}
              disabled
            />

            {signInWorkflowFields?.visitCompany.included === 1 && (
              <OpForm.Input
                label={t('signInManuallyDrawer.company.label')}
                name="company"
                rules={[
                  {
                    required: signInWorkflowFields?.visitCompany.required === 1,
                    message: t('signInManuallyDrawer.company.required'),
                  },
                ]}
              />
            )}

            {signInWorkflowFields?.host.included === 1 && (
              <OpForm.Select
                label={t('signInManuallyDrawer.host.label')}
                name="hostId"
                rules={[
                  {
                    required: signInWorkflowFields.host.required === 1,
                    message: t('signInManuallyDrawer.host.required'),
                  },
                ]}
                placeholder={
                  filteredHosts.length === 0
                    ? t('signInManuallyDrawer.host.placeholderNotAvailable')
                    : t('signInManuallyDrawer.host.placeholder')
                }
                showSearch={{
                  filterOption: (input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase()),
                }}
                options={filteredHosts.map((host) => ({
                  label: host.identity?.fullName || host.identity?.email,
                  value: host.id,
                }))}
                allowClear={hasAllVisitorsWrite}
              />
            )}

            {signInWorkflowFields?.visitPurpose.included === 1 && (
              <OpForm.TextAreaInput
                label={t('signInManuallyDrawer.purpose.label')}
                name="purpose"
                rows={4}
                rules={[
                  {
                    required: signInWorkflowFields?.visitPurpose.required === 1,
                    message: t('signInManuallyDrawer.purpose.required'),
                  },
                ]}
              />
            )}

            {signInWorkflowFields?.visitNotes?.included === 1 &&
              hasVisitorlogWrite && (
                <OpForm.TextAreaInput
                  label={t('signInManuallyDrawer.note.label')}
                  name="note"
                  rows={4}
                  rules={[
                    {
                      required: signInWorkflowFields.visitNotes.required === 1,
                      message: t('signInManuallyDrawer.note.required'),
                    },
                  ]}
                />
              )}

            <OpDivider />
            {renderFormFields()}
          </OpForm>
        }
      />
      <SignInManuallyAccessControlModal
        open={isAccessControlModalOpen}
        newStatus={newStatus}
        visitorList={form.getFieldValue('visitors') || []}
        onClose={() => setIsAccessControlModalOpen(false)}
        onConfirm={handleConfirmedDenied}
      />
    </>
  );
};

export default SignInManuallyDrawer;
